import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle, Textarea } from '../../components/ui/Input'
import { Card, CardHeader, CardBody } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  GatewayPagamento, GATEWAY_LABEL, AmbienteGateway, AMBIENTE_GATEWAY_LABEL,
  ProvedorEmail, PROVEDOR_EMAIL_LABEL,
} from '../../lib/enums'
import { formatDate, formatMoney } from '../../lib/utils'
import type { CredencialGateway, ConfiguracaoEmail, SolicitacaoContato, PerguntaFrequente, PlanoAdmin } from '../../types'

// ── Meios de pagamento ────────────────────────────────────────────────────────

export function MeiosDePagamentoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState<CredencialGateway | null>(null)
  const [novoAberto, setNovoAberto] = useState(false)
  const [form, setForm] = useState<{
    gateway: GatewayPagamento
    ambiente: AmbienteGateway
    chavePublica: string
    chaveSecreta: string
    segredoWebhook: string
    ativo: boolean
    ehPadrao: boolean
  }>({
    gateway: GatewayPagamento.MercadoPago,
    ambiente: AmbienteGateway.Sandbox,
    chavePublica: '',
    chaveSecreta: '',
    segredoWebhook: '',
    ativo: true,
    ehPadrao: false,
  })
  const [removendo, setRemovendo] = useState<CredencialGateway | null>(null)

  const { data: gateways = [], isLoading } = useQuery({ queryKey: ['admin-gateways'], queryFn: adminApi.gateways })

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarGateway(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-gateways'] })
      toast.success('Credenciais salvas.')
      fechar()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const definirPadrao = useMutation({
    mutationFn: (id: string) => adminApi.definirGatewayPadrao(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-gateways'] })
      toast.success('Gateway padrão atualizado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => adminApi.removerGateway(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-gateways'] })
      toast.success('Gateway removido.')
      setRemovendo(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirNovo() {
    setEditando(null)
    setForm({ gateway: GatewayPagamento.MercadoPago, ambiente: AmbienteGateway.Sandbox, chavePublica: '', chaveSecreta: '', segredoWebhook: '', ativo: true, ehPadrao: gateways.length === 0 })
    setNovoAberto(true)
  }

  function abrirEdicao(c: CredencialGateway) {
    setEditando(c)
    setForm({
      gateway: c.gateway, ambiente: c.ambiente,
      chavePublica: c.chavePublica ?? '', chaveSecreta: '', segredoWebhook: '',
      ativo: c.ativo, ehPadrao: c.ehPadrao,
    })
    setNovoAberto(true)
  }

  function fechar() {
    setNovoAberto(false)
    setEditando(null)
  }

  return (
    <>
      <PageHeader title="Meios de pagamento" />
      <Card>
        <CardHeader title="Gateways de cobrança" icon="ri-bank-card-line" />
        <CardBody>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-13 text-muted">
                As chaves são gravadas cifradas e nunca voltam para a tela. O gateway padrão é quem gera os links de pagamento.
              </p>
              <Button icon="ri-add-line" onClick={abrirNovo}>Cadastrar gateway</Button>
            </div>

            {isLoading ? <LoadingState /> : gateways.length === 0 ? (
              <EmptyState
                icon="ri-bank-card-line"
                message="Nenhum meio de pagamento cadastrado. Enquanto isso, as cobranças são registradas e baixadas manualmente."
                action={<Button variant="soft-primary" icon="ri-add-line" onClick={abrirNovo}>Cadastrar gateway</Button>}
              />
            ) : (
              <Table>
                <THead>
                  <tr><Th>Gateway</Th><Th>Ambiente</Th><Th>Chaves</Th><Th>Situação</Th><Th>Atualizado</Th><Th className="text-right">Ações</Th></tr>
                </THead>
                <tbody>
                  {gateways.map((g) => (
                    <Tr key={g.id}>
                      <Td>
                        <span className="flex items-center gap-2 font-medium text-white">
                          {GATEWAY_LABEL[g.gateway]}
                          {g.ehPadrao && <Badge variant="primary">Padrão</Badge>}
                        </span>
                      </Td>
                      <Td className="text-muted">{AMBIENTE_GATEWAY_LABEL[g.ambiente]}</Td>
                      <Td>
                        <span className="flex flex-wrap gap-1">
                          <Badge variant={g.possuiChaveSecreta ? 'income' : 'expense'}>{g.possuiChaveSecreta ? 'secreta ok' : 'sem secreta'}</Badge>
                          {g.possuiSegredoWebhook && <Badge variant="muted">webhook</Badge>}
                        </span>
                      </Td>
                      <Td><Badge variant={g.ativo ? 'income' : 'muted'}>{g.ativo ? 'Ativo' : 'Inativo'}</Badge></Td>
                      <Td className="whitespace-nowrap text-muted">{formatDate(g.updatedAt)}</Td>
                      <Td className="whitespace-nowrap text-right">
                        <IconButton icon="ri-star-line" label={`Tornar ${GATEWAY_LABEL[g.gateway]} padrão`} tone="primary" disabled={g.ehPadrao || !g.ativo} onClick={() => definirPadrao.mutate(g.id)} />
                        <IconButton icon="ri-pencil-fill" label={`Editar ${GATEWAY_LABEL[g.gateway]}`} tone="info" onClick={() => abrirEdicao(g)} />
                        <IconButton icon="ri-delete-bin-5-fill" label={`Remover ${GATEWAY_LABEL[g.gateway]}`} tone="danger" onClick={() => setRemovendo(g)} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Modal
              open={novoAberto}
              onClose={fechar}
              title={editando ? `Editar ${GATEWAY_LABEL[editando.gateway]}` : 'Cadastrar meio de pagamento'}
              footer={
                <>
                  <Button variant="light" onClick={fechar}>Cancelar</Button>
                  <Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
                </>
              }
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Select label="Gateway" value={form.gateway} disabled={!!editando} onChange={(e) => setForm((f) => ({ ...f, gateway: Number(e.target.value) as GatewayPagamento }))}>
                  {Object.entries(GATEWAY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
                <Select label="Ambiente" value={form.ambiente} disabled={!!editando} onChange={(e) => setForm((f) => ({ ...f, ambiente: Number(e.target.value) as AmbienteGateway }))}>
                  {Object.entries(AMBIENTE_GATEWAY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
                <div className="sm:col-span-2">
                  <Input label="Chave pública" value={form.chavePublica} onChange={(e) => setForm((f) => ({ ...f, chavePublica: e.target.value }))} placeholder="Opcional, usada no checkout" autoComplete="off" />
                </div>
                <div className="sm:col-span-2">
                  <Input label="Chave secreta" type="password" value={form.chaveSecreta} onChange={(e) => setForm((f) => ({ ...f, chaveSecreta: e.target.value }))} placeholder={editando ? 'Deixe vazio para manter a atual' : 'Access token ou secret key'} autoComplete="new-password" />
                </div>
                <div className="sm:col-span-2">
                  <Input label="Segredo do webhook" type="password" value={form.segredoWebhook} onChange={(e) => setForm((f) => ({ ...f, segredoWebhook: e.target.value }))} placeholder="Opcional" autoComplete="new-password" />
                </div>
                <div className="rounded bg-surface2 p-3"><Toggle label="Ativo" checked={form.ativo} onChange={(v) => setForm((f) => ({ ...f, ativo: v }))} /></div>
                <div className="rounded bg-surface2 p-3"><Toggle label="Usar como padrão" checked={form.ehPadrao} onChange={(v) => setForm((f) => ({ ...f, ehPadrao: v }))} /></div>
                <p className="text-2xs text-muted sm:col-span-2">
                  Webhook a configurar no gateway: <code className="rounded bg-surface3 px-1">/api/webhooks/{form.gateway}</code>
                </p>
              </div>
            </Modal>

            <ConfirmDialog
              open={!!removendo}
              onClose={() => setRemovendo(null)}
              onConfirm={() => removendo && remover.mutate(removendo.id)}
              loading={remover.isPending}
              message={<>As credenciais de <strong className="text-white">{removendo ? GATEWAY_LABEL[removendo.gateway] : ''}</strong> serão apagadas.</>}
            />
          </div>
        </CardBody>
      </Card>
    </>
  )
}

// ── Planos ────────────────────────────────────────────────────────────────────

type FormPlano = {
  nome: string; descricao: string; precoMensal: number; precoAnual: number; ativo: boolean
  maxContas: string; maxCartoes: string; maxMetas: string; maxOrcamentos: string; maxCompartilhamentos: string
  maxAtualizacoesPluggyPorDia: string
  permiteOpenFinance: boolean; permiteRelatoriosAvancados: boolean; permiteExportacao: boolean
  permiteNotasFiscais: boolean; permiteRecorrencias: boolean; permiteCompartilhamento: boolean
}

// Campos de limite ficam como texto no formulário: vazio = sem limite (null), preenchido = o número.
const paraTexto = (n?: number) => (n == null ? '' : String(n))
const paraNumero = (t: string) => (t.trim() === '' ? undefined : Number(t))

function formularioDoPlano(p: PlanoAdmin): FormPlano {
  return {
    nome: p.nome, descricao: p.descricao ?? '', precoMensal: p.precoMensal, precoAnual: p.precoAnual, ativo: p.ativo,
    maxContas: paraTexto(p.maxContas), maxCartoes: paraTexto(p.maxCartoes), maxMetas: paraTexto(p.maxMetas),
    maxOrcamentos: paraTexto(p.maxOrcamentos), maxCompartilhamentos: paraTexto(p.maxCompartilhamentos),
    maxAtualizacoesPluggyPorDia: paraTexto(p.maxAtualizacoesPluggyPorDia),
    permiteOpenFinance: p.permiteOpenFinance, permiteRelatoriosAvancados: p.permiteRelatoriosAvancados,
    permiteExportacao: p.permiteExportacao, permiteNotasFiscais: p.permiteNotasFiscais,
    permiteRecorrencias: p.permiteRecorrencias, permiteCompartilhamento: p.permiteCompartilhamento,
  }
}

export function PlanosPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState<PlanoAdmin | null>(null)
  const [form, setForm] = useState<FormPlano | null>(null)

  const { data: planos = [], isLoading } = useQuery({ queryKey: ['admin-planos'], queryFn: adminApi.planos })

  const salvar = useMutation({
    mutationFn: () => adminApi.atualizarPlano(editando!.id, {
      nome: form!.nome, descricao: form!.descricao || undefined, precoMensal: form!.precoMensal, precoAnual: form!.precoAnual, ativo: form!.ativo,
      maxContas: paraNumero(form!.maxContas), maxCartoes: paraNumero(form!.maxCartoes), maxMetas: paraNumero(form!.maxMetas),
      maxOrcamentos: paraNumero(form!.maxOrcamentos), maxCompartilhamentos: paraNumero(form!.maxCompartilhamentos),
      maxAtualizacoesPluggyPorDia: paraNumero(form!.maxAtualizacoesPluggyPorDia),
      permiteOpenFinance: form!.permiteOpenFinance, permiteRelatoriosAvancados: form!.permiteRelatoriosAvancados,
      permiteExportacao: form!.permiteExportacao, permiteNotasFiscais: form!.permiteNotasFiscais,
      permiteRecorrencias: form!.permiteRecorrencias, permiteCompartilhamento: form!.permiteCompartilhamento,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-planos'] }); qc.invalidateQueries({ queryKey: ['planos'] }); toast.success('Plano atualizado.'); setEditando(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirEdicao(p: PlanoAdmin) {
    setEditando(p)
    setForm(formularioDoPlano(p))
  }

  return (
    <>
      <PageHeader title="Planos" />
      <Card>
        <CardHeader title="Planos da plataforma" icon="ri-vip-crown-line" />
        <CardBody>
          <div className="space-y-4">
            <p className="text-13 text-muted">
              Edição dos planos existentes — preço, limites e o que cada um libera. Um campo de limite em branco significa sem limite. Não dá pra criar ou remover planos por aqui.
            </p>

            {isLoading ? <LoadingState /> : (
              <Table>
                <THead>
                  <tr><Th>Plano</Th><Th>Preço</Th><Th>Contas/Cartões</Th><Th>Atualizações Pluggy/dia</Th><Th>Situação</Th><Th className="text-right">Ações</Th></tr>
                </THead>
                <tbody>
                  {planos.map((p) => (
                    <Tr key={p.id}>
                      <Td>
                        <p className="font-medium text-white">{p.nome}</p>
                        <p className="text-2xs text-muted">{p.codigo}</p>
                      </Td>
                      <Td className="whitespace-nowrap text-muted">{formatMoney(p.precoMensal)}/mês · {formatMoney(p.precoAnual)}/ano</Td>
                      <Td className="text-muted">{p.maxContas ?? '∞'} / {p.maxCartoes ?? '∞'}</Td>
                      <Td className="text-muted">{p.maxAtualizacoesPluggyPorDia ?? 'sem limite'}</Td>
                      <Td><Badge variant={p.ativo ? 'income' : 'muted'}>{p.ativo ? 'Ativo' : 'Inativo'}</Badge></Td>
                      <Td className="whitespace-nowrap text-right">
                        <IconButton icon="ri-pencil-fill" label={`Editar ${p.nome}`} tone="info" onClick={() => abrirEdicao(p)} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Modal
              open={!!editando}
              onClose={() => setEditando(null)}
              title={`Editar ${editando?.nome ?? ''}`}
              footer={
                <>
                  <Button variant="light" onClick={() => setEditando(null)}>Cancelar</Button>
                  <Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
                </>
              }
            >
              {form && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="Nome" value={form.nome} onChange={(e) => setForm((f) => ({ ...f!, nome: e.target.value }))} />
                  <div className="rounded bg-surface2 p-3 self-end"><Toggle label="Plano ativo" checked={form.ativo} onChange={(v) => setForm((f) => ({ ...f!, ativo: v }))} /></div>
                  <div className="sm:col-span-2"><Textarea label="Descrição" value={form.descricao} onChange={(e) => setForm((f) => ({ ...f!, descricao: e.target.value }))} rows={2} /></div>
                  <Input label="Preço mensal (R$)" type="number" step="0.01" value={form.precoMensal} onChange={(e) => setForm((f) => ({ ...f!, precoMensal: Number(e.target.value) }))} />
                  <Input label="Preço anual (R$)" type="number" step="0.01" value={form.precoAnual} onChange={(e) => setForm((f) => ({ ...f!, precoAnual: Number(e.target.value) }))} />

                  <Input label="Máx. contas" placeholder="sem limite" value={form.maxContas} onChange={(e) => setForm((f) => ({ ...f!, maxContas: e.target.value }))} />
                  <Input label="Máx. cartões" placeholder="sem limite" value={form.maxCartoes} onChange={(e) => setForm((f) => ({ ...f!, maxCartoes: e.target.value }))} />
                  <Input label="Máx. metas" placeholder="sem limite" value={form.maxMetas} onChange={(e) => setForm((f) => ({ ...f!, maxMetas: e.target.value }))} />
                  <Input label="Máx. orçamentos" placeholder="sem limite" value={form.maxOrcamentos} onChange={(e) => setForm((f) => ({ ...f!, maxOrcamentos: e.target.value }))} />
                  <Input label="Máx. compartilhamentos" placeholder="sem limite" value={form.maxCompartilhamentos} onChange={(e) => setForm((f) => ({ ...f!, maxCompartilhamentos: e.target.value }))} />
                  <Input label="Atualizações Pluggy por dia" placeholder="sem limite" value={form.maxAtualizacoesPluggyPorDia} onChange={(e) => setForm((f) => ({ ...f!, maxAtualizacoesPluggyPorDia: e.target.value }))} />

                  <div className="sm:col-span-2 grid gap-2 sm:grid-cols-2">
                    <div className="rounded bg-surface2 p-3"><Toggle label="Open Finance" checked={form.permiteOpenFinance} onChange={(v) => setForm((f) => ({ ...f!, permiteOpenFinance: v }))} /></div>
                    <div className="rounded bg-surface2 p-3"><Toggle label="Relatórios avançados" checked={form.permiteRelatoriosAvancados} onChange={(v) => setForm((f) => ({ ...f!, permiteRelatoriosAvancados: v }))} /></div>
                    <div className="rounded bg-surface2 p-3"><Toggle label="Exportação de dados" checked={form.permiteExportacao} onChange={(v) => setForm((f) => ({ ...f!, permiteExportacao: v }))} /></div>
                    <div className="rounded bg-surface2 p-3"><Toggle label="Notas fiscais" checked={form.permiteNotasFiscais} onChange={(v) => setForm((f) => ({ ...f!, permiteNotasFiscais: v }))} /></div>
                    <div className="rounded bg-surface2 p-3"><Toggle label="Recorrências" checked={form.permiteRecorrencias} onChange={(v) => setForm((f) => ({ ...f!, permiteRecorrencias: v }))} /></div>
                    <div className="rounded bg-surface2 p-3"><Toggle label="Compartilhamento" checked={form.permiteCompartilhamento} onChange={(v) => setForm((f) => ({ ...f!, permiteCompartilhamento: v }))} /></div>
                  </div>
                </div>
              )}
            </Modal>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

// ── E-mail ────────────────────────────────────────────────────────────────────

export function EmailPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState<ConfiguracaoEmail | null>(null)
  const [novoAberto, setNovoAberto] = useState(false)
  const [testando, setTestando] = useState<ConfiguracaoEmail | null>(null)
  const [destinoTeste, setDestinoTeste] = useState('')
  const [removendo, setRemovendo] = useState<ConfiguracaoEmail | null>(null)
  const [form, setForm] = useState({
    provedor: ProvedorEmail.Smtp as ProvedorEmail,
    remetenteEmail: '', remetenteNome: '',
    smtpHost: '', smtpPorta: 587, smtpUsuario: '', smtpSenha: '', smtpUsarSsl: true,
    resendApiKey: '', sendGridApiKey: '',
    ativo: true, ehPadrao: false,
  })

  const { data: emails = [], isLoading } = useQuery({ queryKey: ['admin-email'], queryFn: adminApi.emails })

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarEmail(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-email'] }); toast.success('Configuração salva.'); fechar() },
    onError: (e: Error) => toast.error(e.message),
  })

  const definirPadrao = useMutation({
    mutationFn: (id: string) => adminApi.definirEmailPadrao(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-email'] }); toast.success('Provedor padrão atualizado.') },
    onError: (e: Error) => toast.error(e.message),
  })

  const testar = useMutation({
    mutationFn: () => adminApi.testarEmail(testando!.id, destinoTeste),
    onSuccess: () => { toast.success('E-mail de teste enviado.'); setTestando(null) },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível enviar. Confira as credenciais.'),
  })

  const remover = useMutation({
    mutationFn: (id: string) => adminApi.removerEmail(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-email'] }); toast.success('Configuração removida.'); setRemovendo(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirNovo() {
    setEditando(null)
    setForm({
      provedor: ProvedorEmail.Smtp, remetenteEmail: '', remetenteNome: 'Walleon',
      smtpHost: '', smtpPorta: 587, smtpUsuario: '', smtpSenha: '', smtpUsarSsl: true,
      resendApiKey: '', sendGridApiKey: '', ativo: true, ehPadrao: emails.length === 0,
    })
    setNovoAberto(true)
  }

  function abrirEdicao(c: ConfiguracaoEmail) {
    setEditando(c)
    setForm({
      provedor: c.provedor, remetenteEmail: c.remetenteEmail, remetenteNome: c.remetenteNome,
      smtpHost: c.smtpHost ?? '', smtpPorta: c.smtpPorta ?? 587, smtpUsuario: c.smtpUsuario ?? '', smtpSenha: '', smtpUsarSsl: c.smtpUsarSsl,
      resendApiKey: '', sendGridApiKey: '', ativo: c.ativo, ehPadrao: c.ehPadrao,
    })
    setNovoAberto(true)
  }

  function fechar() {
    setNovoAberto(false)
    setEditando(null)
  }

  return (
    <>
      <PageHeader title="E-mail" />
      <Card>
        <CardHeader title="Provedores de e-mail" icon="ri-mail-settings-line" />
        <CardBody>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-13 text-muted">
                Configure SMTP, Resend e SendGrid ao mesmo tempo; o provedor marcado como padrão é quem envia os e-mails de verdade (redefinição de senha, avisos de contato).
              </p>
              <Button icon="ri-add-line" onClick={abrirNovo}>Cadastrar provedor</Button>
            </div>

            {isLoading ? <LoadingState /> : emails.length === 0 ? (
              <EmptyState
                icon="ri-mail-settings-line"
                message="Nenhum provedor de e-mail configurado. Sem isso, redefinição de senha e avisos de contato não são enviados."
                action={<Button variant="soft-primary" icon="ri-add-line" onClick={abrirNovo}>Cadastrar provedor</Button>}
              />
            ) : (
              <Table>
                <THead>
                  <tr><Th>Provedor</Th><Th>Remetente</Th><Th>Situação</Th><Th>Atualizado</Th><Th className="text-right">Ações</Th></tr>
                </THead>
                <tbody>
                  {emails.map((c) => (
                    <Tr key={c.id}>
                      <Td>
                        <span className="flex items-center gap-2 font-medium text-white">
                          {PROVEDOR_EMAIL_LABEL[c.provedor]}
                          {c.ehPadrao && <Badge variant="primary">Padrão</Badge>}
                        </span>
                      </Td>
                      <Td className="text-muted">{c.remetenteNome} &lt;{c.remetenteEmail}&gt;</Td>
                      <Td><Badge variant={c.ativo ? 'income' : 'muted'}>{c.ativo ? 'Ativo' : 'Inativo'}</Badge></Td>
                      <Td className="whitespace-nowrap text-muted">{c.updatedAt ? formatDate(c.updatedAt) : '—'}</Td>
                      <Td className="whitespace-nowrap text-right">
                        <IconButton icon="ri-star-line" label={`Tornar ${PROVEDOR_EMAIL_LABEL[c.provedor]} padrão`} tone="primary" disabled={c.ehPadrao || !c.ativo} onClick={() => definirPadrao.mutate(c.id)} />
                        <IconButton icon="ri-send-plane-line" label={`Testar ${PROVEDOR_EMAIL_LABEL[c.provedor]}`} tone="info" onClick={() => { setTestando(c); setDestinoTeste('') }} />
                        <IconButton icon="ri-pencil-fill" label={`Editar ${PROVEDOR_EMAIL_LABEL[c.provedor]}`} tone="info" onClick={() => abrirEdicao(c)} />
                        <IconButton icon="ri-delete-bin-5-fill" label={`Remover ${PROVEDOR_EMAIL_LABEL[c.provedor]}`} tone="danger" onClick={() => setRemovendo(c)} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Modal
              open={novoAberto}
              onClose={fechar}
              title={editando ? `Editar ${PROVEDOR_EMAIL_LABEL[editando.provedor]}` : 'Cadastrar provedor de e-mail'}
              footer={
                <>
                  <Button variant="light" onClick={fechar}>Cancelar</Button>
                  <Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
                </>
              }
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Select label="Provedor" value={form.provedor} disabled={!!editando} onChange={(e) => setForm((f) => ({ ...f, provedor: Number(e.target.value) as ProvedorEmail }))}>
                  {Object.entries(PROVEDOR_EMAIL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
                <div />
                <Input label="E-mail do remetente" value={form.remetenteEmail} onChange={(e) => setForm((f) => ({ ...f, remetenteEmail: e.target.value }))} placeholder="contato@walleon.com.br" />
                <Input label="Nome do remetente" value={form.remetenteNome} onChange={(e) => setForm((f) => ({ ...f, remetenteNome: e.target.value }))} placeholder="Walleon" />

                {form.provedor === ProvedorEmail.Smtp ? (
                  <>
                    <div className="sm:col-span-2"><Input label="Host SMTP" value={form.smtpHost} onChange={(e) => setForm((f) => ({ ...f, smtpHost: e.target.value }))} placeholder="smtp.seuprovedor.com" /></div>
                    <Input label="Porta" type="number" value={form.smtpPorta} onChange={(e) => setForm((f) => ({ ...f, smtpPorta: Number(e.target.value) }))} />
                    <div className="rounded bg-surface2 p-3 self-end"><Toggle label="Usar SSL/TLS" checked={form.smtpUsarSsl} onChange={(v) => setForm((f) => ({ ...f, smtpUsarSsl: v }))} /></div>
                    <Input label="Usuário" value={form.smtpUsuario} onChange={(e) => setForm((f) => ({ ...f, smtpUsuario: e.target.value }))} autoComplete="off" />
                    <Input label="Senha" type="password" value={form.smtpSenha} onChange={(e) => setForm((f) => ({ ...f, smtpSenha: e.target.value }))} placeholder={editando ? 'Deixe vazio para manter a atual' : ''} autoComplete="new-password" />
                  </>
                ) : form.provedor === ProvedorEmail.SendGrid ? (
                  <div className="sm:col-span-2">
                    <Input label="API Key do SendGrid" type="password" value={form.sendGridApiKey} onChange={(e) => setForm((f) => ({ ...f, sendGridApiKey: e.target.value }))} placeholder={editando ? 'Deixe vazio para manter a atual' : 'SG....'} autoComplete="new-password" />
                    <p className="mt-1 text-12 text-muted">Crie a chave com a permissão só de "Mail Send" e verifique o remetente (ou o domínio) no SendGrid.</p>
                  </div>
                ) : (
                  <div className="sm:col-span-2">
                    <Input label="API Key do Resend" type="password" value={form.resendApiKey} onChange={(e) => setForm((f) => ({ ...f, resendApiKey: e.target.value }))} placeholder={editando ? 'Deixe vazio para manter a atual' : 're_...'} autoComplete="new-password" />
                  </div>
                )}

                <div className="rounded bg-surface2 p-3"><Toggle label="Ativo" checked={form.ativo} onChange={(v) => setForm((f) => ({ ...f, ativo: v }))} /></div>
                <div className="rounded bg-surface2 p-3"><Toggle label="Usar como padrão" checked={form.ehPadrao} onChange={(v) => setForm((f) => ({ ...f, ehPadrao: v }))} /></div>
              </div>
            </Modal>

            <Modal
              open={!!testando}
              onClose={() => setTestando(null)}
              title="Enviar e-mail de teste"
              size="sm"
              footer={
                <>
                  <Button variant="light" onClick={() => setTestando(null)}>Cancelar</Button>
                  <Button icon="ri-send-plane-line" loading={testar.isPending} disabled={!destinoTeste} onClick={() => testar.mutate()}>Enviar</Button>
                </>
              }
            >
              <Input label="Enviar para" type="email" value={destinoTeste} onChange={(e) => setDestinoTeste(e.target.value)} placeholder="seu@email.com" />
            </Modal>

            <ConfirmDialog
              open={!!removendo}
              onClose={() => setRemovendo(null)}
              onConfirm={() => removendo && remover.mutate(removendo.id)}
              loading={remover.isPending}
              message={<>A configuração de <strong className="text-white">{removendo ? PROVEDOR_EMAIL_LABEL[removendo.provedor] : ''}</strong> será apagada.</>}
            />
          </div>
        </CardBody>
      </Card>
    </>
  )
}

// ── Contatos ──────────────────────────────────────────────────────────────────

export function ContatosPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [filtro, setFiltro] = useState<'' | 'pendentes' | 'respondidas'>('pendentes')
  const [aberto, setAberto] = useState<SolicitacaoContato | null>(null)
  const [resposta, setResposta] = useState('')

  const { data: contatos = [], isLoading } = useQuery({
    queryKey: ['admin-contatos', filtro],
    queryFn: () => adminApi.contatos(filtro === '' ? undefined : filtro === 'respondidas'),
  })

  const responder = useMutation({
    mutationFn: () => adminApi.responderContato(aberto!.id, resposta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-contatos'] })
      toast.success('Resposta enviada por e-mail.')
      setAberto(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <PageHeader title="Contatos" />
      <Card>
        <CardHeader title="Mensagens do 'Fale conosco'" icon="ri-customer-service-2-line" />
        <CardBody>
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-56">
                <Select value={filtro} onChange={(e) => setFiltro(e.target.value as typeof filtro)} aria-label="Filtro">
                  <option value="pendentes">Pendentes</option>
                  <option value="respondidas">Respondidas</option>
                  <option value="">Todas</option>
                </Select>
              </div>
              <span className="text-13 text-muted">{contatos.length} mensagens</span>
            </div>

            {isLoading ? <LoadingState /> : contatos.length === 0 ? (
              <EmptyState icon="ri-customer-service-2-line" message="Nenhuma mensagem por aqui." />
            ) : (
              <Table>
                <THead>
                  <tr><Th>Contato</Th><Th>Mensagem</Th><Th>Recebida</Th><Th>Situação</Th><Th className="text-right">Ações</Th></tr>
                </THead>
                <tbody>
                  {contatos.map((c) => (
                    <Tr key={c.id}>
                      <Td>
                        <p className="font-medium text-white">{c.nome}</p>
                        <p className="text-2xs text-muted">{c.email}{c.telefone ? ` · ${c.telefone}` : ''}</p>
                      </Td>
                      <Td className="max-w-[320px] truncate text-muted" title={c.mensagem}>{c.mensagem}</Td>
                      <Td className="whitespace-nowrap text-muted">{formatDate(c.criadaEm)}</Td>
                      <Td><Badge variant={c.respondida ? 'income' : 'warning'}>{c.respondida ? 'Respondida' : 'Pendente'}</Badge></Td>
                      <Td className="whitespace-nowrap text-right">
                        <IconButton icon={c.respondida ? 'ri-eye-line' : 'ri-reply-line'} label={c.respondida ? `Ver resposta para ${c.nome}` : `Responder ${c.nome}`} tone="primary" onClick={() => { setAberto(c); setResposta(c.respostaTexto ?? '') }} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Modal
              open={!!aberto}
              onClose={() => setAberto(null)}
              title={`Mensagem de ${aberto?.nome ?? ''}`}
              footer={
                aberto?.respondida ? <Button variant="light" onClick={() => setAberto(null)}>Fechar</Button> : (
                  <>
                    <Button variant="light" onClick={() => setAberto(null)}>Cancelar</Button>
                    <Button icon="ri-send-plane-line" loading={responder.isPending} disabled={!resposta.trim()} onClick={() => responder.mutate()}>Enviar resposta</Button>
                  </>
                )
              }
            >
              <div className="space-y-4">
                <div className="rounded bg-surface2 p-3">
                  <p className="text-2xs text-muted">{aberto?.email}{aberto?.telefone ? ` · ${aberto.telefone}` : ''} · {aberto ? formatDate(aberto.criadaEm) : ''}</p>
                  <p className="mt-2 whitespace-pre-wrap text-13 text-white">{aberto?.mensagem}</p>
                </div>
                {aberto?.respondida ? (
                  <div className="rounded bg-surface2 p-3">
                    <p className="text-2xs text-muted">Respondida {aberto.respondidaEm ? formatDate(aberto.respondidaEm) : ''}</p>
                    <p className="mt-2 whitespace-pre-wrap text-13 text-white">{aberto.respostaTexto}</p>
                  </div>
                ) : (
                  <Textarea label="Sua resposta (enviada por e-mail para o contato)" value={resposta} onChange={(e) => setResposta(e.target.value)} rows={5} />
                )}
              </div>
            </Modal>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

// ── FAQ ───────────────────────────────────────────────────────────────────────

export function FaqPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState<PerguntaFrequente | null>(null)
  const [novoAberto, setNovoAberto] = useState(false)
  const [removendo, setRemovendo] = useState<PerguntaFrequente | null>(null)
  const [form, setForm] = useState({ pergunta: '', resposta: '', ordem: 0, ativa: true })

  const { data: perguntas = [], isLoading } = useQuery({ queryKey: ['admin-faq'], queryFn: adminApi.faq })

  const salvar = useMutation({
    mutationFn: () => editando ? adminApi.atualizarFaq(editando.id, form) : adminApi.criarFaq(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-faq'] }); toast.success('Pergunta salva.'); fechar() },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => adminApi.removerFaq(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-faq'] }); toast.success('Pergunta removida.'); setRemovendo(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirNovo() {
    setEditando(null)
    setForm({ pergunta: '', resposta: '', ordem: perguntas.length, ativa: true })
    setNovoAberto(true)
  }

  function abrirEdicao(p: PerguntaFrequente) {
    setEditando(p)
    setForm({ pergunta: p.pergunta, resposta: p.resposta, ordem: p.ordem, ativa: p.ativa })
    setNovoAberto(true)
  }

  function fechar() {
    setNovoAberto(false)
    setEditando(null)
  }

  return (
    <>
      <PageHeader title="Dúvidas frequentes" />
      <Card>
        <CardHeader title="FAQ do site institucional" icon="ri-question-line" />
        <CardBody>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-13 text-muted">Essas perguntas alimentam a aba "Dúvidas e tutoriais" do site institucional.</p>
              <Button icon="ri-add-line" onClick={abrirNovo}>Nova pergunta</Button>
            </div>

            {isLoading ? <LoadingState /> : perguntas.length === 0 ? (
              <EmptyState icon="ri-question-line" message="Nenhuma pergunta cadastrada." action={<Button variant="soft-primary" icon="ri-add-line" onClick={abrirNovo}>Nova pergunta</Button>} />
            ) : (
              <Table>
                <THead>
                  <tr><Th>Ordem</Th><Th>Pergunta</Th><Th>Situação</Th><Th className="text-right">Ações</Th></tr>
                </THead>
                <tbody>
                  {[...perguntas].sort((a, b) => a.ordem - b.ordem).map((p) => (
                    <Tr key={p.id}>
                      <Td className="text-muted">{p.ordem}</Td>
                      <Td className="max-w-[420px] truncate font-medium text-white" title={p.pergunta}>{p.pergunta}</Td>
                      <Td><Badge variant={p.ativa ? 'income' : 'muted'}>{p.ativa ? 'Visível' : 'Oculta'}</Badge></Td>
                      <Td className="whitespace-nowrap text-right">
                        <IconButton icon="ri-pencil-fill" label={`Editar pergunta ${p.pergunta}`} tone="info" onClick={() => abrirEdicao(p)} />
                        <IconButton icon="ri-delete-bin-5-fill" label={`Remover pergunta ${p.pergunta}`} tone="danger" onClick={() => setRemovendo(p)} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Modal
              open={novoAberto}
              onClose={fechar}
              title={editando ? 'Editar pergunta' : 'Nova pergunta'}
              footer={
                <>
                  <Button variant="light" onClick={fechar}>Cancelar</Button>
                  <Button icon="ri-save-line" loading={salvar.isPending} disabled={!form.pergunta.trim() || !form.resposta.trim()} onClick={() => salvar.mutate()}>Salvar</Button>
                </>
              }
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-3"><Input label="Pergunta" value={form.pergunta} onChange={(e) => setForm((f) => ({ ...f, pergunta: e.target.value }))} /></div>
                <div className="sm:col-span-3"><Textarea label="Resposta" value={form.resposta} onChange={(e) => setForm((f) => ({ ...f, resposta: e.target.value }))} rows={4} /></div>
                <Input label="Ordem" type="number" value={form.ordem} onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) }))} />
                <div className="col-span-2 rounded bg-surface2 p-3 self-end"><Toggle label="Visível no site" checked={form.ativa} onChange={(v) => setForm((f) => ({ ...f, ativa: v }))} /></div>
              </div>
            </Modal>

            <ConfirmDialog
              open={!!removendo}
              onClose={() => setRemovendo(null)}
              onConfirm={() => removendo && remover.mutate(removendo.id)}
              loading={remover.isPending}
              message={<>A pergunta <strong className="text-white">{removendo?.pergunta}</strong> será removida do site.</>}
            />
          </div>
        </CardBody>
      </Card>
    </>
  )
}

// ── Configurações gerais ────────────────────────────────────────────────────

export function GeralPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({ queryKey: ['admin-configuracao-geral'], queryFn: adminApi.configuracaoGeral })
  const [emailContato, setEmailContato] = useState('')

  useEffect(() => { if (data) setEmailContato(data.emailContato) }, [data])

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarConfiguracaoGeral({ emailContato }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-configuracao-geral'] }); toast.success('Configuração salva.') },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <PageHeader title="Configurações gerais" />
      <Card>
        <CardHeader title="Dados gerais da plataforma" icon="ri-settings-3-line" />
        <CardBody>
          {isLoading ? <LoadingState /> : (
            <div className="max-w-md space-y-4">
              <Input
                label="E-mail de contato do site"
                type="email"
                value={emailContato}
                onChange={(e) => setEmailContato(e.target.value)}
                placeholder="contato@walleon.com.br"
              />
              <p className="text-2xs text-muted">
                Usado no site institucional e como destino dos avisos de novas mensagens do formulário "Fale conosco".
              </p>
              <Button icon="ri-save-line" loading={salvar.isPending} disabled={!emailContato.trim()} onClick={() => salvar.mutate()}>Salvar</Button>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

// ── Armazenamento (Cloudflare R2) ───────────────────────────────────────────

export function ArmazenamentoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState({ accountId: '', endpoint: '', bucket: '', accessKeyId: '', secretAccessKey: '' })
  const [resultadoTeste, setResultadoTeste] = useState<{ ok: boolean; detalhe: string } | null>(null)
  const [confirmRemover, setConfirmRemover] = useState(false)

  const { data, isLoading } = useQuery({ queryKey: ['admin-armazenamento'], queryFn: adminApi.armazenamento })

  useEffect(() => {
    if (!data) return
    setForm({ accountId: data.accountId ?? '', endpoint: data.endpoint ?? '', bucket: data.bucket ?? '', accessKeyId: data.accessKeyId ?? '', secretAccessKey: '' })
  }, [data])

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarArmazenamento(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-armazenamento'] }); toast.success('Configuração salva.'); setResultadoTeste(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  const testar = useMutation({
    mutationFn: adminApi.testarArmazenamento,
    onSuccess: (r) => setResultadoTeste(r),
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: adminApi.removerArmazenamento,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-armazenamento'] }); toast.success('Credenciais removidas.'); setResultadoTeste(null); setConfirmRemover(false) },
    onError: (e: Error) => toast.error(e.message),
  })

  const [acompanhando, setAcompanhando] = useState(false)

  // Se a página foi recarregada no meio de uma migração já em andamento, retoma o acompanhamento.
  useEffect(() => {
    adminApi.statusMigracaoArmazenamento().then((r) => { if (r.emAndamento) setAcompanhando(true) }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A migração roda em segundo plano no servidor (pode levar minutos com arquivo grande) — o clique só
  // inicia, e aqui a tela fica perguntando o andamento em vez de segurar uma requisição aberta.
  const status = useQuery({
    queryKey: ['admin-armazenamento-migracao'],
    queryFn: adminApi.statusMigracaoArmazenamento,
    enabled: acompanhando,
    refetchInterval: (query) => (query.state.data?.emAndamento ? 1500 : false),
  })

  useEffect(() => {
    if (!acompanhando || !status.data || status.data.emAndamento) return
    setAcompanhando(false)
    const r = status.data
    if (r.falhas === 0) toast.success(`${r.migrados} arquivo(s) migrado(s) pro R2 (${r.jaEstavam} já estavam lá).`)
    else toast.error(`${r.migrados} migrado(s), ${r.falhas} falharam. Veja o console pra detalhes.`)
    if (r.erros.length > 0) console.error('Falhas na migração pro R2:', r.erros)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data, acompanhando])

  const migrar = useMutation({
    mutationFn: adminApi.migrarArmazenamento,
    onSuccess: () => setAcompanhando(true),
    onError: (e: Error) => toast.error(e.message),
  })

  const migrando = migrar.isPending || acompanhando

  return (
    <>
      <PageHeader title="Armazenamento" />
      <Card>
        <CardHeader title="Cloudflare R2" icon="ri-database-2-line" />
        <CardBody>
          {isLoading ? <LoadingState /> : (
            <div className="max-w-2xl space-y-5">
              <div className="flex items-center justify-between gap-3 rounded bg-surface2 p-3">
                <p className="text-13 text-muted">
                  Espaço de armazenamento de arquivos da plataforma — hoje usado pelos pacotes de atualização do
                  app (APK e OTA), e vai passar a valer pras próximas features que precisarem guardar arquivo.{' '}
                  {data?.configurado ? 'Configurado — os arquivos vão pro R2.' : 'Sem configurar — os arquivos continuam indo pro disco local da VPS.'}
                </p>
                <Badge variant={data?.configurado ? 'income' : 'warning'}>{data?.configurado ? 'Configurado' : 'Não configurado'}</Badge>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Input label="Account ID (Cloudflare)" value={form.accountId} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))} placeholder="ex.: 8f2b1c..." autoComplete="off" />
                <Input label="Balde (bucket)" value={form.bucket} onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))} placeholder="walleon-atualizacoes" autoComplete="off" />
                <Input label="Access Key ID" value={form.accessKeyId} onChange={(e) => setForm((f) => ({ ...f, accessKeyId: e.target.value }))} autoComplete="off" />
                <Input
                  label="Secret Access Key" type="password" value={form.secretAccessKey}
                  onChange={(e) => setForm((f) => ({ ...f, secretAccessKey: e.target.value }))}
                  placeholder={data?.possuiSecretAccessKey ? 'Deixe vazio para manter a atual' : ''}
                  autoComplete="new-password"
                />
              </div>

              <details className="rounded border border-border/60 p-3">
                <summary className="cursor-pointer text-13 font-medium text-white">Avançado</summary>
                <div className="mt-3">
                  <Input
                    label="Endpoint (opcional)" value={form.endpoint} onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
                    placeholder={form.accountId ? `https://${form.accountId}.r2.cloudflarestorage.com` : 'https://<account id>.r2.cloudflarestorage.com'}
                  />
                  <p className="mt-1 text-2xs text-muted">Só preencha se o endereço não seguir o padrão calculado a partir do Account ID.</p>
                </div>
              </details>

              {resultadoTeste && (
                <div className={`flex items-center gap-2 rounded px-3 py-2.5 text-13 ${resultadoTeste.ok ? 'bg-income/10 text-income' : 'bg-expense/10 text-expense'}`} role="status">
                  <i className={resultadoTeste.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} aria-hidden /> {resultadoTeste.detalhe}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
                <Button variant="light" icon="ri-plug-line" loading={testar.isPending} disabled={!data?.configurado} onClick={() => testar.mutate()}>Testar conexão</Button>
                <Button variant="light" icon="ri-upload-cloud-2-line" loading={migrando} disabled={!data?.configurado || migrando} onClick={() => migrar.mutate()}>
                  {acompanhando ? `Migrando... (${status.data?.migrados ?? 0} ok, ${status.data?.jaEstavam ?? 0} já tinha, ${status.data?.falhas ?? 0} falhas)` : 'Migrar arquivos do disco pro R2'}
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmRemover(true)}
                  disabled={!data?.configurado}
                  className="ml-auto text-13 text-expense hover:underline disabled:opacity-40"
                >
                  Remover credenciais
                </button>
              </div>

              <p className="text-2xs text-muted">
                {data?.updatedAt ? `Última alteração em ${formatDate(data.updatedAt)}.` : 'Ainda não configurado.'}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmRemover}
        onClose={() => setConfirmRemover(false)}
        onConfirm={() => remover.mutate()}
        loading={remover.isPending}
        title="Remover credenciais do R2?"
        confirmLabel="Sim, remover"
        message="Os pacotes de atualização voltam a ser guardados no disco local da VPS."
      />
    </>
  )
}
